// @flow

import { useEffect, useCallback, useRef } from 'react';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import * as WidgetPropsModel from '../../../models/widget-props';
import ColHeatmap from '../../widgets-ui/col-heatmap';
import { buildWidgetDateString } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import { formatNumbersWithUnits } from '../../../utils/formatNumbers';
import { processPeakTimeHeatmapWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/peak-time-heatmap';
import { browserQueryAPI } from '../../../services/query-api-browser';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const PeakTimeHeatmapWidget = ({
  widget,
  availableMetrics,
  locations,
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.peakTimeHeatmapT) => {
  const [loading, setLoading] = useSafeState(false);
  const [error, setError] = useSafeState(false);
  const [autoTitle, setAutoTitle] = useSafeState('');
  const [widgetDescription, setWidgetDescription] = useSafeState('');
  const [tableRows, setTableRows] = useSafeState([]);
  const [tableColumns, setTableColumns] = useSafeState([]);
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [currentElement, isVisible] = useSafeVisible((vi) => uiOptions.disableViewportLoading || vi > 0);
  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);

  const usingComps =
    !!widget.comparisonPeriod &&
    widget.comparisonPeriod.active &&
    widget.showChangeValue;

  const loadWidgetData = async () => {
    setLoading(true);
    setError(false);
    reportLoading();

    try {
      // Build config for the package
      const config = {
        metric: widget.metric,
        locations: widget.locations,
        period: widget.period,
        comparisonPeriod: widget.comparisonPeriod,
        demographicFilter: widget.demographicFilter,
        showChangeValue: widget.showChangeValue,
        ...(widget.customTitle && { customTitle: widget.customTitle }),
      };

      const deps = {
        queryAPI: browserQueryAPI,
        availableMetrics: availableMetrics,
        locations: locations,
        startOfWeek: 'monday',
      };

      const widgetData = await processPeakTimeHeatmapWidget(config, deps);

      // Use title from package
      setAutoTitle(widgetData.title);

      // Extract columns and rows from widgetData
      // widgetData.columns: [{ header: 'Hour', ... }, { header: 'Mon', ... }, ...]
      // widgetData.rows: [[{ value: '00:00', ... }, { value: 123, ... }, ...], ...]

      // Transform to match the UI component's expected format
      // UI expects: tableColumns = [{ label: 'Mon', unitType, rows: [...] }, ...]
      // and tableRows = [{ label: '00:00' }, ...]

      const dayColumns = widgetData.columns.slice(1); // Skip first column (Hour)
      const builtTableColumns = dayColumns.map((col, colIndex) => {
        const colRows = widgetData.rows.map(row => {
          const cell = row[colIndex + 1]; // +1 to skip hour column
          if (cell && cell.value !== null) {
            return {
              value: cell.value,
              changeValue: widget.showChangeValue ? cell.value : undefined,
              unitType: cell.unit,
            };
          }
          return null;
        });

        return {
          label: col.header,
          unitType: col.defaultUnit,
          rows: colRows,
        };
      });

      const builtTableRows = widgetData.rows.map(row => ({
        label: row[0].value, // First column is hour label
      }));

      setTableColumns(builtTableColumns);
      setTableRows(builtTableRows);

      // Set description if needed
      if (!uiOptions.disableDescription) {
        const metric = {
          ...widget.metric,
          details: AvailableMetricsModel.findMetricDetails(
            availableMetrics,
            widget.metric.metricGroupKey,
            widget.metric.metricKey
          ),
        };
        if (metric.details) {
          const demographicFilter = widget.metric.demographicFilter || widget.demographicFilter;
          const demos = Object.values(demographicFilter ?? {}).filter(Boolean).join(" ");
          const desc = AvailableMetricsModel.getMetricDescription(metric.details);
          setWidgetDescription(demos.length ? `${desc} (${demos})` : desc);
        }
      }

      // Report completion to context - use package output directly
      reportComplete(widgetData);

      setLoading(false);
    } catch (e) {
      setError(true);
      setLoading(false);
      reportError(e.message || 'Failed to load Peak Time Heatmap widget');
      console.error(
        'API call or something else failed for Peak Time Heatmap widget',
        e
      );
    }
  };

  const title = widget.customTitle || autoTitle;

  const [fetchRequired, setFetchRequired] = useSafeState(true);
  const hasInitialFetchedRef = useRef(false);

  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) setFetchRequired(true);
  }, [hasWidgetChanged]);

  useEffect(() => {
    if (fetchRequired && isVisible) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;

      const metric = {
        ...widget.metric,
        details: AvailableMetricsModel.findMetricDetails(
          availableMetrics,
          widget.metric.metricGroupKey,
          widget.metric.metricKey
        )
      };

      if (!metric || !metric.details) {
        console.error(
          `No metric found in availableMetrics for the Peak Time Heatmap widget: ${widget.metric.metricGroupKey}:${widget.metric.metricKey}`
        );
        setError(true);
      } else {
        loadWidgetData();
      }
    }
  }, [widget, fetchRequired, isVisible, locations]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtitle = buildWidgetDateString(widget, true);

  return (
    <div ref={currentElement}>
      <ColHeatmap
        errors={error}
        loading={loading}
        title={title}
        description={widgetDescription}
        subtitle={subtitle}
        tableColumns={tableColumns}
        tableRows={tableRows}
        dataKey={widget.showChangeValue ? 'changeValue' : 'value'}
        shadeBy="table"
      />
    </div>
  );
};

export default PeakTimeHeatmapWidget;
